import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SITE_NAME = 'Sampaoli Creazioni';

interface ProductSeoHeadProps {
  title: string;
  description: string;
  canonicalUrl: string;
  imageUrl: string;
  imageAlt: string;
}

/**
 * Sets document <title>, meta description, canonical, OG, Twitter,
 * hreflang alternates and locale tags for product pages.
 * Cleans up on unmount to restore defaults.
 */
export function ProductSeoHead({ title, description, canonicalUrl, imageUrl, imageAlt }: ProductSeoHeadProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'it';

  useEffect(() => {
    const prev = document.title;
    document.title = title;

    const ogLocale = lang === 'it' ? 'it_IT' : 'en_US';
    const ogLocaleAlt = lang === 'it' ? 'en_US' : 'it_IT';
    document.documentElement.setAttribute('lang', lang);

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[(.+?)="(.+?)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      return el;
    };

    const setLink = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.href = href;
      return el;
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', 'index, follow');
    setLink('canonical', canonicalUrl);
    setLink('alternate', canonicalUrl, 'it');
    setLink('alternate', canonicalUrl, 'en');
    setLink('alternate', canonicalUrl, 'x-default');

    // OG
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', imageUrl);
    setMeta('meta[property="og:image:alt"]', 'content', imageAlt);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:type"]', 'content', 'product');
    setMeta('meta[property="og:locale"]', 'content', ogLocale);
    setMeta('meta[property="og:locale:alternate"]', 'content', ogLocaleAlt);

    // Twitter
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', imageUrl);
    setMeta('meta[name="twitter:image:alt"]', 'content', imageAlt);

    return () => {
      document.title = prev;
    };
  }, [title, description, canonicalUrl, imageUrl, imageAlt, lang]);

  return null;
}
